import spinnerIcon from '@iconify/icons-material-symbols/progress-activity';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { ErrorMessageBlock } from '#pie/components/error-message/ErrorMessageBlock';
import { Icon } from '#pie/components/icon/Icon';
import { InputSearch } from '#pie/components/input-search/InputSearch';
import { KnowledgeRecent } from '#pie/components/knowledge-recent/KnowledgeRecent';
import { KnowledgeTabAll } from '#pie/components/knowledge-tabs/KnowledgeTabAll';
import { KnowledgeTabSelected } from '#pie/components/knowledge-tabs/KnowledgeTabSelected';
import { Page, PageHeader } from '#pie/components/page/Page';
import { PageHead } from '#pie/components/page-head/PageHead';
import { Stack } from '#pie/components/stack/Stack';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '#pie/components/tabs';
import { Text } from '#pie/components/text/Text';
import { useGetKnowledgeArticle } from '#pie/internal/api/pie-bff';
import { emptyFilterAtom } from '#pie/stores/searchOnlyFilter';
import { highlightText } from '#pie/utils/hightLightedText';

enum KnowledgeTabs {
  All = 'alle',
  Selected = 'geselecteerd',
}

export const KnowledgeBase = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const tab = location.pathname.split('/').at(2) as KnowledgeTabs;
  const filter = useAtomValue(emptyFilterAtom);

  const { data, error, isLoading } = useGetKnowledgeArticle(
    {
      filter: filter,
      pagination: {
        pageIndex: 0,
        pageSize: 500,
      },
    },
    {
      query: {
        select: (data) => ({
          ...data,
          items: data.items.sort((a, b) => {
            return (
              new Date(b.modifiedOn).getTime() -
              new Date(a.modifiedOn).getTime()
            );
          }),
        }),
      },
    },
  );

  const searchItems = useMemo(() => {
    const searchQuery = search?.toLowerCase() || '';
    return (
      data?.items
        .filter((item) => {
          const title = item.title?.toLowerCase() || '';
          const content = item.content?.toLowerCase() || '';
          return title.includes(searchQuery) || content.includes(searchQuery);
        })
        .map((m) => ({
          ...m,
          ...highlightText(m.content, search),
        }))
        .sort((a, b) => b.numberOfMatches - a.numberOfMatches) ?? []
    );
  }, [data?.items, search]);

  return (
    <>
      <PageHead title={t('common.title.knowledge_base')} />
      <Page>
        <PageHeader title={t('common.title.knowledge_base')}>
          <div className="mr-4">
            <InputSearch
              placeholder={t(
                'knowledge_base.article.header.search_placeholder',
              )}
              onChange={(e) => setSearch(e.target.value)}
              value={search || ''}
              onClear={() => setSearch('')}
              width="500px"
              className="active:border-subtle-active focus:border-subtle-focus focus:ring-0"
            />
            {search && (
              <div className="absolute shadow-menu bg-white w-125 max-h-100 overflow-y-auto border border-border-minimal-enabled rounded-input">
                {searchItems.length ? (
                  searchItems.map((article) => (
                    <div
                      key={article.id}
                      className="p-2 hover:bg-surface-minimal-teal cursor-pointer px-4 py-3"
                    >
                      <Link
                        to={`/kennisbank/${article.id}`}
                        data-testid="search-result"
                      >
                        <div
                          // biome-ignore lint/security/noDangerouslySetInnerHtml: <because it's necessary>
                          dangerouslySetInnerHTML={{
                            __html: highlightText(article.title, search).html,
                          }}
                        />
                      </Link>
                    </div>
                  ))
                ) : isLoading ? (
                  <Icon
                    role="progressbar"
                    aria-label="loading"
                    icon={spinnerIcon}
                    size={32}
                    className="m-auto animate-spin text-content-tertiary opacity-50"
                  />
                ) : (
                  <Text className="p-4 text-content-tertiary">
                    {t('common.no_results')}
                  </Text>
                )}
              </div>
            )}
          </div>
        </PageHeader>
        <KnowledgeRecent data={data} isLoading={isLoading} />
        <Tabs
          asChild
          value={tab}
          onValueChange={(val) => navigate(`../${val}`)}
        >
          {error != null ? (
            <ErrorMessageBlock error={error} />
          ) : (
            <Stack gap="lg">
              <TabsList>
                <TabsTrigger value={KnowledgeTabs.Selected}>
                  {t('knowledge_base.tab.selected')}
                </TabsTrigger>
                <TabsTrigger value={KnowledgeTabs.All}>
                  {t('knowledge_base.tab.all')}
                </TabsTrigger>
              </TabsList>
              <TabsContent value={KnowledgeTabs.Selected}>
                <KnowledgeTabSelected data={data} isLoading={isLoading} />
              </TabsContent>
              <TabsContent value={KnowledgeTabs.All}>
                <KnowledgeTabAll data={data} isLoading={isLoading} />
              </TabsContent>
            </Stack>
          )}
        </Tabs>
      </Page>
    </>
  );
};
